import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Calendar, Clock, BookOpen, ChevronDown, ChevronRight, Printer } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import { formatDate, generateId } from '../../utils/helpers';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';
import DateInput from '../../components/common/DateInput';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const typeColors = {
  'formative': 'bg-blue-100 text-blue-700 border-blue-200',
  'summative': 'bg-blue-100 text-blue-700 border-blue-200',
  'slip-test': 'bg-orange-100 text-orange-700 border-orange-200',
  'revision': 'bg-green-100 text-green-700 border-green-200',
};

const typeLabels = {
  'formative': 'Formative Assessment',
  'summative': 'Summative Assessment',
  'slip-test': 'Slip Test',
  'revision': 'Revision Test',
};

const subjectColors = [
  'bg-indigo-50 border-indigo-200 text-indigo-700',
  'bg-green-50 border-green-200 text-green-700',
  'bg-orange-50 border-orange-200 text-orange-700',
  'bg-pink-50 border-pink-200 text-pink-700',
  'bg-teal-50 border-teal-200 text-teal-700',
  'bg-blue-50 border-blue-200 text-blue-700',
  'bg-yellow-50 border-yellow-200 text-yellow-700',
  'bg-red-50 border-red-200 text-red-700',
];

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function ExamSchedule() {
  const { exams, setExams, examTimetable, setExamTimetable, classes, subjects, students, parents, staff } = useApp();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'teacher';
  const isAdminOnly = currentUser?.role === 'admin';
  const isTeacherRole = currentUser?.role === 'teacher';
  const teacherProfile = isTeacherRole ? staff.find(s => s.id === currentUser?.profileId) : null;
  const teacherClassIds = teacherProfile?.classIds || [];
  const teacherSubjectIds = teacherProfile?.subjectIds || [];

  // Parent: resolve children for selector
  const isParent = currentUser?.role === 'parent';
  const parentRecord = isParent ? parents.find(p => p.id === currentUser?.profileId) : null;
  const myChildren = parentRecord ? students.filter(s => parentRecord.studentIds?.includes(s.id)) : [];
  const [selectedChildId, setSelectedChildId] = useState(myChildren[0]?.id || null);
  const selectedChild = myChildren.find(c => c.id === selectedChildId) || myChildren[0];

  // Determine which classId(s) to filter by
  const myClassIds = (() => {
    if (currentUser?.role === 'student') {
      const s = students.find(s => s.id === currentUser?.profileId);
      return s ? [s.classId] : [];
    }
    if (isParent) {
      return selectedChild ? [selectedChild.classId] : myChildren.map(c => c.classId);
    }
    if (isTeacherRole) return teacherClassIds.length ? teacherClassIds : null;
    return null; // admin — no filter
  })();

  // Filter exam timetable entries to only those relevant to this user's class(es) and subject(s)
  const filterEntries = (entries) => {
    let result = entries;
    if (myClassIds) result = result.filter(e => (e.classIds || []).some(cid => myClassIds.includes(cid)));
    if (isTeacherRole && teacherSubjectIds.length) result = result.filter(e => teacherSubjectIds.includes(e.subjectId));
    return result;
  };

  // Filter exams list to only those with at least one timetable entry for this student's class
  const visibleExams = (myClassIds
    ? exams.filter(exam => {
        const entries = examTimetable.filter(e => e.examId === exam.id);
        return filterEntries(entries).length > 0;
      })
    : exams).slice().reverse();

  const [expandedExams, setExpandedExams] = useState({});
  const [filterClass, setFilterClass] = useState('');

  // Exam modal
  const [examModal, setExamModal] = useState(false);
  const [examForm, setExamForm] = useState({ name: '', type: 'formative', startDate: '', endDate: '', status: 'scheduled', academicYear: '2025-2026', classIds: [] });
  const [editingExam, setEditingExam] = useState(null);

  // Timetable entry modal
  const [entryModal, setEntryModal] = useState(false);
  const [entryExamId, setEntryExamId] = useState('');
  const [entryRows, setEntryRows] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);

  const blankRow = (allClassIds) => ({ _key: Date.now() + Math.random(), date: '', subjectId: '', startTime: '09:00', endTime: '12:00', classIds: allClassIds });

  const openAddExam = () => {
    setExamForm({ name: '', type: 'formative', startDate: '', endDate: '', status: 'scheduled', academicYear: '2025-2026', classIds: classes.map(c => c.id) });
    setEditingExam(null);
    setExamModal(true);
  };
  const openEditExam = (e) => { setExamForm(e); setEditingExam(e); setExamModal(true); };
  const removeExam = (id) => { if (window.confirm('Delete exam?')) setExams(prev => prev.filter(e => e.id !== id)); };
  const saveExam = () => {
    if (editingExam) setExams(prev => prev.map(e => e.id === editingExam.id ? { ...examForm, id: editingExam.id } : e));
    else setExams(prev => [...prev, { ...examForm, id: generateId('EXM') }]);
    setExamModal(false);
  };

  const openAddEntry = (examId) => {
    const allClassIds = classes.map(c => c.id);
    setEntryExamId(examId);
    setEntryRows([blankRow(allClassIds)]);
    setEditingEntry(null);
    setEntryModal(true);
  };
  const openEditEntry = (entry) => {
    setEntryExamId(entry.examId);
    setEntryRows([{ _key: entry.id, date: entry.date, subjectId: entry.subjectId, startTime: entry.startTime, endTime: entry.endTime, classIds: entry.classIds || [] }]);
    setEditingEntry(entry);
    setEntryModal(true);
  };
  const removeEntry = (id) => { if (window.confirm('Remove this schedule entry?')) setExamTimetable(prev => prev.filter(e => e.id !== id)); };

  const saveEntries = () => {
    const valid = entryRows.filter(r => r.date && r.subjectId);
    if (editingEntry) {
      const r = valid[0];
      if (r) setExamTimetable(prev => prev.map(e => e.id === editingEntry.id ? { ...e, date: r.date, subjectId: r.subjectId, startTime: r.startTime, endTime: r.endTime, classIds: r.classIds } : e));
    } else {
      const newEntries = valid.map(r => ({ id: generateId('ET'), examId: entryExamId, date: r.date, subjectId: r.subjectId, startTime: r.startTime, endTime: r.endTime, classIds: r.classIds }));
      setExamTimetable(prev => [...prev, ...newEntries]);
    }
    setEntryModal(false);
  };

  const updateRow = (key, field, val) => setEntryRows(rows => rows.map(r => r._key === key ? { ...r, [field]: val } : r));
  const toggleRowClass = (key, classId) => setEntryRows(rows => rows.map(r => r._key === key ? { ...r, classIds: r.classIds.includes(classId) ? r.classIds.filter(id => id !== classId) : [...r.classIds, classId] } : r));
  const addRow = () => setEntryRows(rows => [...rows, blankRow(classes.map(c => c.id))]);
  const removeRow = (key) => setEntryRows(rows => rows.filter(r => r._key !== key));

  const today = new Date().toISOString().split('T')[0];

  const printTimetable = async (exam, entries) => {
    const school = await getSchoolInfo();
    // Group entries by classId
    const classBlocks = classes.map(cls => {
      const clsEntries = entries.filter(e => (e.classIds || []).includes(cls.id))
        .sort((a, b) => a.date.localeCompare(b.date));
      if (clsEntries.length === 0) return null;
      const rows = clsEntries.map((e, i) => {
        const sub = subjects.find(s => s.id === e.subjectId);
        const dateObj = new Date(e.date + 'T00:00:00');
        const day = DAYS[dateObj.getDay()];
        return `<tr>
          <td>${i + 1}</td>
          <td><strong>${formatDate(e.date)}</strong></td>
          <td>${day}</td>
          <td>${formatTime12(e.startTime)} – ${formatTime12(e.endTime)}</td>
          <td><strong>${sub?.name || '—'}</strong> <span style="color:#888">(${sub?.code || '—'})</span></td>
        </tr>`;
      }).join('');
      return `
        <div class="class-block">
          <div class="class-title">${cls.name} – Section ${cls.section}</div>
          <table>
            <thead><tr><th>#</th><th>Date</th><th>Day</th><th>Time</th><th>Subject</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    }).filter(Boolean).join('');

    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.open(); win.document.write(`<!DOCTYPE html>
<html><head><title>Exam Timetable — ${exam.name}</title>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap" rel="stylesheet">
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:'Roboto',sans-serif; padding:24px 32px; color:#111; font-size:12px; }
  .school-header { margin-bottom:16px; }
  .meta { display:flex; justify-content:space-between; margin-bottom:20px; font-size:12px; color:#555; }
  .meta .title { font-size:15px; font-weight:700; color:#1e3a8a; }
  .class-block { margin-bottom:20px; page-break-inside:avoid; }
  .class-title { font-size:13px; font-weight:700; color:#fff; background:#1e3a8a; padding:6px 12px; border-radius:6px 6px 0 0; }
  table { width:100%; border-collapse:collapse; }
  thead th { background:#f1f5f9; color:#374151; font-weight:600; font-size:11px; padding:7px 10px; text-align:left; border-bottom:2px solid #d1d5db; }
  td { padding:6px 10px; border-bottom:1px solid #f3f4f6; font-size:12px; }
  tr:last-child td { border-bottom:none; }
  .footer { text-align:center; font-size:11px; color:#aaa; margin-top:24px; padding-top:8px; border-top:1px dashed #e5e7eb; }
  .print-btn { text-align:center; margin-top:16px; }
  @media print { .print-btn{display:none;} body{padding:12px;} }
</style></head><body>
<div class="school-header">
  ${schoolHeader(school, '#1e3a8a', `Examination Timetable &mdash; ${exam.name} &mdash; Academic Year ${exam.academicYear || '2025-2026'}`)}
</div>
<div class="meta">
  <div><div class="title">${exam.name}</div><div style="margin-top:2px">${formatDate(exam.startDate)} – ${formatDate(exam.endDate)}</div></div>
  <div style="text-align:right">Date: <strong>${(() => { const d = new Date(); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}</strong></div>
</div>
${classBlocks}
<div class="footer">Computer-generated &mdash; ${school.name}</div>
<div class="print-btn"><button onclick="window.print()" style="background:#1e3a8a;color:#fff;border:none;padding:10px 28px;border-radius:6px;font-size:13px;cursor:pointer;font-family:'Roboto',sans-serif">🖨 Print</button></div>
</body></html>`); win.document.close();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Exam Schedule</h1>
          <p className="text-gray-500 text-sm">{visibleExams.length} exam{visibleExams.length !== 1 ? 's' : ''} this academic year</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Child selector for parents with multiple children */}
          {isParent && myChildren.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {myChildren.map(c => (
                <button
                  key={c.id}
                  onClick={() => setSelectedChildId(c.id)}
                  className={`px-4 py-1 rounded-lg text-sm font-medium transition-colors border ${
                    c.id === selectedChild?.id
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {c.firstName} {c.lastName}
                </button>
              ))}
            </div>
          )}
          {isAdmin && (
            <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
              <option value="">All Classes</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}-{c.section}</option>)}
            </select>
          )}
          {isAdminOnly && (
            <button onClick={openAddExam} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1 rounded-lg text-sm font-medium">
              <Plus size={16} /> Schedule Exam
            </button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {visibleExams.map(exam => {
          const isOpen = expandedExams[exam.id] !== false; // default open
          const entries = filterEntries(
            examTimetable.filter(e => e.examId === exam.id)
          ).filter(e => !filterClass || (e.classIds || []).includes(filterClass))
           .sort((a, b) => a.date.localeCompare(b.date));

          return (
            <div key={exam.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Exam header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <button
                  onClick={() => setExpandedExams(prev => ({ ...prev, [exam.id]: !isOpen }))}
                  className="flex items-center gap-3 flex-1 text-left"
                >
                  <div className={`p-2 rounded-lg border ${typeColors[exam.type] || 'bg-gray-100 border-gray-200'}`}>
                    <Calendar size={16} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold">{exam.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full font-medium border ${typeColors[exam.type] || 'bg-gray-100 border-gray-200'}`}>
                        {typeLabels[exam.type] || exam.type}
                      </span>
                      <Badge value={exam.status} />
                    </div>
                    <p className="text-sm mt-0.5">
                      {formatDate(exam.startDate)} – {formatDate(exam.endDate)}
                      <span className="mx-2">·</span>
                      {exam.academicYear}
                      <span className="mx-2">·</span>
                      {entries.length} subject{entries.length !== 1 ? 's' : ''} scheduled
                    </p>
                  </div>
                  <span className="ml-2">
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </span>
                </button>
                <div className="flex items-center gap-1 ml-4">
                  {entries.length > 0 && (
                    <button onClick={() => printTimetable(exam, entries)} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50">
                      <Printer size={13}/> Print
                    </button>
                  )}
                  {isAdminOnly && (
                    <>
                      <button onClick={() => openAddEntry(exam.id)} className="flex items-center gap-1 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 px-3 py-1.5 rounded-lg font-medium">
                        <Plus size={13} /> Add Subject
                      </button>
                      <button onClick={() => openEditExam(exam)} className="p-1.5 hover:bg-indigo-50 rounded-lg text-indigo-500"><Edit2 size={14} /></button>
                      <button onClick={() => removeExam(exam.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              </div>

              {/* Date-wise timetable */}
              {isOpen && (
                <div>
                  {entries.length === 0 ? (
                    <p className="text-center py-6">No subjects scheduled yet</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-100">
                          <th className="px-4 py-2 text-left font-semibold">Date</th>
                          <th className="px-4 py-2 text-left font-semibold">Day</th>
                          <th className="px-4 py-2 text-left font-semibold">Time</th>
                          <th className="px-4 py-2 text-left font-semibold">Subject</th>
                          {isAdmin && <th className="px-4 py-2 text-left font-semibold">Classes</th>}
                          {isAdminOnly && <th className="px-4 py-2 text-center font-semibold w-16">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // compute rowspan per date
                          const dateCount = {};
                          entries.forEach(e => { dateCount[e.date] = (dateCount[e.date] || 0) + 1; });
                          const dateSeen = {};
                          return entries.map((entry, idx) => {
                          const subject = subjects.find(s => s.id === entry.subjectId);
                          const dateObj = new Date(entry.date + 'T00:00:00');
                          const dayName = DAYS[dateObj.getDay()];
                          const isToday = entry.date === today;
                          const isPast = entry.date < today;
                          const colorClass = subjectColors[idx % subjectColors.length];
                          const isFirstOfDate = !dateSeen[entry.date];
                          if (isFirstOfDate) dateSeen[entry.date] = true;
                          const span = dateCount[entry.date];
                          return (
                            <tr key={entry.id} className={isToday ? 'bg-indigo-50' : isPast ? 'bg-gray-50/40' : 'hover:bg-gray-50'}>
                              {isFirstOfDate && (
                                <td rowSpan={span} className="px-4 py-1 font-semibold whitespace-nowrap align-middle border-r border-gray-100" style={{ color: isToday ? '#3730a3' : isPast ? '#9ca3af' : '#111' }}>
                                  {formatDate(entry.date)}
                                </td>
                              )}
                              {isFirstOfDate && (
                                <td rowSpan={span} className="px-4 py-1 whitespace-nowrap align-middle border-r border-gray-100" style={{ color: isToday ? '#6366f1' : isPast ? '#9ca3af' : '#555' }}>
                                  {dayName}{isToday && <span className="ml-1.5 bg-indigo-600 text-white px-1.5 py-0.5 rounded-full text-xs">Today</span>}
                                </td>
                              )}
                              <td className="px-4 py-1 whitespace-nowrap">
                                <span className="flex items-center gap-1">
                                  <Clock size={12} className="opacity-40"/>
                                  {formatTime12(entry.startTime)} – {formatTime12(entry.endTime)}
                                </span>
                              </td>
                              <td className="px-4 py-1">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border font-semibold ${colorClass}`}>
                                  <BookOpen size={12}/>{subject?.name || '—'} <span className="font-normal opacity-60">({subject?.code || '—'})</span>
                                </span>
                              </td>
                              {isAdmin && (
                                <td className="px-4 py-1">
                                  <div className="flex flex-wrap gap-1">
                                    {(entry.classIds || []).map(cid => {
                                      const cls = classes.find(c => c.id === cid);
                                      return cls ? <span key={cid} className="bg-gray-100 px-2 py-0.5 rounded font-medium">{cls.name}-{cls.section}</span> : null;
                                    })}
                                  </div>
                                </td>
                              )}
                              {isAdminOnly && (
                                <td className="px-4 py-1 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button onClick={() => openEditEntry(entry)} className="p-1 hover:bg-indigo-50 rounded text-indigo-400"><Edit2 size={13}/></button>
                                    <button onClick={() => removeEntry(entry.id)} className="p-1 hover:bg-red-50 rounded text-red-400"><Trash2 size={13}/></button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        });
                        })()}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Exam Modal — admin only */}
      <Modal isOpen={examModal} onClose={() => setExamModal(false)} title={editingExam ? 'Edit Exam' : 'Schedule Exam'} size="lg">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Exam Name</label>
            <input value={examForm.name || ''} onChange={e => setExamForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <select value={examForm.type || 'unit-test'} onChange={e => setExamForm(f => ({ ...f, type: e.target.value }))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                {Object.entries(typeLabels).map(([val, label]) => <option key={val} value={val}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select value={examForm.status || 'scheduled'} onChange={e => setExamForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
                {['scheduled', 'ongoing', 'completed'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <DateInput value={examForm.startDate || ''} onChange={e => setExamForm(f => ({ ...f, startDate: e.target.value }))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <DateInput value={examForm.endDate || ''} onChange={e => setExamForm(f => ({ ...f, endDate: e.target.value }))} className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => setExamModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={saveExam} className="px-4 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
        </div>
      </Modal>

      {/* Timetable Entry Modal */}
      <Modal isOpen={entryModal} onClose={() => setEntryModal(false)} title={editingEntry ? 'Edit Schedule Entry' : 'Add Subjects to Schedule'} size="xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm mb-2">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left font-semibold w-6">#</th>
                <th className="px-2 py-2 text-left font-semibold">Date</th>
                <th className="px-2 py-2 text-left font-semibold">Subject</th>
                <th className="px-2 py-2 text-left font-semibold">Start</th>
                <th className="px-2 py-2 text-left font-semibold">End</th>
                <th className="px-2 py-2 text-left font-semibold">Classes</th>
                {!editingEntry && <th className="px-2 py-2 w-6"></th>}
              </tr>
            </thead>
            <tbody>
              {entryRows.map((row, idx) => (
                <tr key={row._key} className="border-b border-gray-100">
                  <td className="px-3 py-2 text-center">{idx + 1}</td>
                  <td className="px-2 py-2">
                    <DateInput value={row.date} onChange={e => updateRow(row._key, 'date', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 w-36" />
                  </td>
                  <td className="px-2 py-2">
                    <select value={row.subjectId} onChange={e => updateRow(row._key, 'subjectId', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 w-36">
                      <option value="">Select</option>
                      {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2">
                    <input type="time" value={row.startTime} onChange={e => updateRow(row._key, 'startTime', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 w-28" />
                  </td>
                  <td className="px-2 py-2">
                    <input type="time" value={row.endTime} onChange={e => updateRow(row._key, 'endTime', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 w-28" />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-wrap gap-1">
                      {classes.map(c => (
                        <button key={c.id} type="button" onClick={() => toggleRowClass(row._key, c.id)}
                          className={`px-1.5 py-0.5 rounded text-xs font-medium border transition-colors ${
                            (row.classIds || []).includes(c.id)
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white border-gray-300 hover:bg-gray-50'
                          }`}>
                          {c.name}-{c.section}
                        </button>
                      ))}
                    </div>
                  </td>
                  {!editingEntry && (
                    <td className="px-2 py-2 text-center">
                      {entryRows.length > 1 && (
                        <button onClick={() => removeRow(row._key)} className="text-red-400 hover:text-red-600 p-0.5">
                          <Trash2 size={13}/>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {!editingEntry && (
            <button onClick={addRow} className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm font-medium px-3 mb-2 mt-1">
              <Plus size={13}/> Add Row
            </button>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-3 pt-3 border-t">
          <button onClick={() => setEntryModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={saveEntries} className="px-4 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            {editingEntry ? 'Save' : `Save ${entryRows.filter(r => r.date && r.subjectId).length || ''} Subject${entryRows.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </Modal>
    </div>
  );
}
export default ExamSchedule;
